<template>
  <div class="numeric-input-wrapper" :class="{ 'has-error': hasError }">
    <div class="numeric-input-field">
      <slot name="currency-symbol">
        <span v-if="currencySymbol" class="currency-symbol">{{ currencySymbol }}</span>
      </slot>
      <input
        ref="inputRef"
        type="text"
        inputmode="decimal"
        :value="displayValue"
        @input="handleInput"
        @keydown="handleKeydown"
        @keyup="handleKeyup"
        @paste="handlePaste"
        @blur="handleBlur"
        @focusout="handleFocusout"
        @focus="handleFocus"
        :aria-invalid="hasError"
        :aria-describedby="hasError && showErrorMessage ? 'error-message' : undefined"
        aria-label="Numeric input"
      />
      <slot name="suffix"></slot>
    </div>
    <div v-if="hasError && showErrorMessage" id="error-message" class="error-message" role="alert" aria-live="polite">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, defineProps, defineEmits, defineExpose } from 'vue';

const props = defineProps({
  min: {
    type: Number,
    default: -Infinity
  },
  max: {
    type: Number,
    default: 100
  },
  value: {
    type: Number,
    default: undefined
  },
  decimalPlaces: {
    type: Number,
    default: 2,
    validator: (value) => value >= 0 && value <= 10
  },
  currencySymbol: {
    type: String,
    default: ''
  },
  showErrorMessage: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['input', 'input-change', 'validation-error', 'keyup', 'focusout']);

const inputRef = ref(null);
const displayValue = ref('');
const errorMessage = ref('');
const isFocused = ref(false);
const cursorPosition = ref(0);
const isTouched = ref(false);

const hasError = computed(() => errorMessage.value !== '' && isTouched.value);

// Utility functions
const isValidNumericChar = (char) => {
  return /^[0-9.-]$/.test(char);
};

const isValidDecimalFormat = (value) => {
  // Allow empty string, single minus sign, or proper decimal format
  if (value === '' || value === '-') return true;

  // Check for valid number format
  const numericRegex = /^-?(\d+\.?\d*|\.\d+)$/;
  if (!numericRegex.test(value)) return false;

  // Check for multiple decimal points
  const dotCount = (value.match(/\./g) || []).length;
  if (dotCount > 1) return false;

  // Check decimal places limit
  const decimalIndex = value.indexOf('.');
  if (decimalIndex !== -1) {
    const decimalPart = value.substring(decimalIndex + 1);
    if (decimalPart.length > props.decimalPlaces) return false;
  }

  return true;
};

const formatNumber = (value) => {
  if (value === '' || value === '-' || isNaN(value)) return value;

  const num = parseFloat(value);
  if (isNaN(num)) return value;

  // Check if the original value contains a decimal point
  const hasDecimalPoint = value.toString().includes('.');

  // If it's an integer (no decimal point), return as integer
  if (!hasDecimalPoint && num === Math.floor(num)) {
    return num.toString();
  }

  // If it has decimal places or is not a whole number, format with specified decimal places
  return num?.toFixed(props.decimalPlaces);
};

const validateRange = (numericValue) => {
  if (isNaN(numericValue)) return false;
  return numericValue >= props.min && numericValue <= props.max;
};

const setError = (message) => {
  errorMessage.value = message;
  emit('validation-error', { message, value: displayValue.value });
};

const clearError = () => {
  errorMessage.value = '';
};

const preserveCursorPosition = async () => {
  await nextTick();
  if (inputRef.value && isFocused.value) {
    inputRef.value.setSelectionRange(cursorPosition.value, cursorPosition.value);
  }
};

const emitValue = (value) => {
  const numericValue = value === '' || value === '-' ? undefined : parseFloat(value);

  if (numericValue !== undefined && !isNaN(numericValue)) {
    if (validateRange(numericValue)) {
      clearError();
      emit('input', numericValue);
      emit('input-change', numericValue);
    } else {
      if (numericValue < props.min) {
        const formattedMin = props.min.toLocaleString();
        setError(`The minimum donation amount is ${props.currencySymbol}${formattedMin}`);
      } else {
        const formattedMax = props.max.toLocaleString();
        setError(`The maximum donation amount is ${props.currencySymbol}${formattedMax}`);
      }
    }
  } else if (value === '' || value === undefined) {
    clearError();
    emit('input', undefined);
    emit('input-change', undefined);
  }
};

// Event handlers
const handleInput = (event) => {
  const inputValue = event.target.value;
  cursorPosition.value = event.target.selectionStart;

  // Mark as touched when user starts typing
  if (!isTouched.value) {
    isTouched.value = true;
  }

  if (!isValidDecimalFormat(inputValue)) {
    // Prevent invalid format
    event.target.value = displayValue.value;
    preserveCursorPosition();
    return;
  }

  displayValue.value = inputValue;
  emitValue(inputValue);
};

const handleKeydown = (event) => {
  const char = event.key;
  const currentValue = event.target.value;
  const selectionStart = event.target.selectionStart;
  const selectionEnd = event.target.selectionEnd;

  // Allow control keys
  if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(char)) {
    return;
  }

  // Allow Ctrl/Cmd combinations
  if (event.ctrlKey || event.metaKey) {
    return;
  }

  // Check if character is valid
  if (!isValidNumericChar(char)) {
    event.preventDefault();
    return;
  }

  // Prevent multiple decimal points
  if (char === '.' && currentValue.includes('.')) {
    event.preventDefault();
    return;
  }

  // Prevent multiple minus signs or minus not at the beginning
  if (char === '-') {
    if (currentValue.includes('-') || selectionStart !== 0) {
      event.preventDefault();
      return;
    }
  }

  // Check decimal places limit
  const decimalIndex = currentValue.indexOf('.');
  if (decimalIndex !== -1 && selectionStart > decimalIndex) {
    const currentDecimalPlaces = currentValue.length - decimalIndex - 1;
    if (currentDecimalPlaces >= props.decimalPlaces && selectionStart === selectionEnd) {
      event.preventDefault();
      return;
    }
  }
};

const handleKeyup = (event) => {
  // Emit the keyup event to parent
  emit('keyup', event);
};

const handlePaste = (event) => {
  event.preventDefault();
  const pastedText = (event.clipboardData || window.clipboardData).getData('text');
  const currentValue = event.target.value;
  const selectionStart = event.target.selectionStart;
  const selectionEnd = event.target.selectionEnd;

  // Create the new value after paste
  const newValue = currentValue.substring(0, selectionStart) + pastedText + currentValue.substring(selectionEnd);

  // Validate the pasted content
  if (isValidDecimalFormat(newValue)) {
    displayValue.value = newValue;
    cursorPosition.value = selectionStart + pastedText.length;
    preserveCursorPosition();
    emitValue(newValue);
  }
};

const handleBlur = () => {
  isFocused.value = false;

  // Mark as touched when field loses focus
  if (!isTouched.value) {
    isTouched.value = true;
  }

  // Format the value on blur if it's valid
  if (displayValue.value && displayValue.value !== '-') {
    const numericValue = parseFloat(displayValue.value);
    if (!isNaN(numericValue)) {
      const formatted = formatNumber(displayValue.value); // Pass the original string value
      displayValue.value = formatted;
      emitValue(numericValue);
    }
  }
};

const handleFocusout = (event) => {
  // Emit the focusout event to parent
  emit('focusout', event);
};

const handleFocus = () => {
  isFocused.value = true;
};

// Watch for external changes to value prop
watch(
  () => props.value,
  (newValue) => {
    if (newValue != null && !isNaN(newValue)) {
      if (!isFocused.value) {
        // When setting from external prop, treat as integer if it's a whole number
        displayValue.value = newValue === Math.floor(newValue) ? newValue.toString() : newValue?.toFixed(props.decimalPlaces);
      }
      if (!validateRange(newValue)) {
        setError('Please enter an amount');
      } else {
        clearError();
      }
    } else {
      displayValue.value = '';
      // Reset touched state when value is cleared externally
      if (newValue === undefined) {
        isTouched.value = false;
      }
      clearError();
    }
  },
  { immediate: true }
);

// Watch for changes to min/max props
watch([() => props.min, () => props.max], () => {
  if (displayValue.value && displayValue.value !== '-') {
    const numericValue = parseFloat(displayValue.value);
    if (!isNaN(numericValue)) {
      emitValue(numericValue);
    }
  }
});

// Initialize display value
if (props.value !== undefined) {
  displayValue.value = props.value === Math.floor(props.value) ? props.value.toString() : props.value?.toFixed(props.decimalPlaces);
}

// Expose refs for parent components
defineExpose({
  inputRef
});
</script>
